import React, { useEffect, useState } from "react"
import "semantic-ui-css/components/modal.min.css"
import "semantic-ui-css/components/dimmer.min.css"
import "./career-plans.css"
import { FormattedMessage } from "react-intl"
import {
  WingsEyebrow,
  WingsHeading,
  WingsText,
  WingsButton,
  WingsIcon,
} from "@wingspans-ui"
import { navigate } from "../../link"
import CareerPlanPanel from "../career-plans/CareerPlanPanel"
import useCareerPlans from "../career-plans/useCareerPlans"
import UpdateCareerPlanModal from "../career-plans/UpdateCareerPlanModal"
import UpdateMilestoneModal from "../career-plans/UpdateMilestoneModal"
import { useQueryParam, StringParam } from "use-query-params"

/**
 * Component to load all career plans for a user.
 */
const CareerPlans = () => {
  // null means no plan is being edited, an object means a plan is being edited
  const [planToEdit, setPlanToEdit] = useState(null)
  // null means no milestone is being edited, an object means a milestone is being edited
  const [milestoneToEdit, setMilestoneToEdit] = useState(null)
  const [newPlanId, setNewPlanId] = useQueryParam("newPlanId", StringParam)
  const {
    careerPlans,
    errorMessage,
    updateCareerPlan,
    updateMilestone,
  } = useCareerPlans()

  // After a guided or unguided career plan is added, scroll to it
  useEffect(() => {
    if (!newPlanId || !careerPlans) {
      return
    }
    const planElement = document.getElementById(newPlanId)
    if (!planElement) {
      return
    }
    planElement.scrollIntoView({ behavior: "smooth", block: "center" })
    setNewPlanId(undefined)
  }, [newPlanId, careerPlans])

  const onSavePlan = async (updatedPlan) => {
    const result = await updateCareerPlan(planToEdit.id, updatedPlan)
    if (!result.errorMessage) {
      setNewPlanId(result.planId)
    }
    return result.errorMessage
  }

  const onSaveMilestone = async (updatedMilestone) => {
    return await updateMilestone(planToEdit.id, milestoneToEdit.id, updatedMilestone)
  }

  return (
    <section className="career-plan-tab w-flex w-flex-column w-items-stretch w-gap-3">
      <header className="w-mx-auto w-text-center">
        <WingsEyebrow>
          <FormattedMessage id="career-plan-eyebrow" defaultMessage="CAREER" />
        </WingsEyebrow>
        <WingsHeading level={1} className="w-mb-1">
          <FormattedMessage id="career-plan-heading" defaultMessage="Plans" />
        </WingsHeading>
        <WingsText className="w-mx-auto w-mb-2 w-max-w-65ch">
          <FormattedMessage
            id="career-plan-overview-copy"
            defaultMessage="Create a personalized timeline with our pre-populated templates or start from scratch to build your own."
          />
        </WingsText>
        <div className="w-flex w-flex-row w-items-center w-justify-center w-gap-1">
          <WingsButton iconName="plus" variant="primary" size="small" onClick={() => navigate("/create-a-plan/")}>
            <FormattedMessage id="career-plans-use-template" defaultMessage="Use Template" />
          </WingsButton>
          <WingsButton
            iconName="plus"
            variant="secondary"
            size="small"
            onClick={() => setPlanToEdit({})}>
            <FormattedMessage id="career-plans-start-from-scratch" defaultMessage="Start From Scratch" />
          </WingsButton>
        </div>
      </header>
      {errorMessage ? (
        <WingsText className="w-mx-auto">
          {errorMessage}
        </WingsText>
      ) : careerPlans === null ? (
        <WingsIcon
          className="w-mx-auto"
          icon="loading"
          spin={true}
          color="var(--w-color-blue-500)"
          size="xl"
        />
      ) : careerPlans.length === 0 ? (
        null
      ) : (
        careerPlans.map(p => (
          <CareerPlanPanel
            key={p.id}
            id={p.id}
            title={p.title}
            mode={"EDIT"}
            color={p.color}
            milestones={p.milestones}
            onEdit={() => setPlanToEdit(p)}
            onMilestoneEdit={(m) => {setMilestoneToEdit(m); setPlanToEdit(p)}}
            onDeleteMilestone={(mId) => updateMilestone(p.id, mId, null, true)}
            toggleMilestoneCompletion={mId => {
              const milestone = p.milestones.find(m => m.id === mId)
              if (!milestone) return
              updateMilestone(p.id, mId, {...milestone, completed: !milestone.completed}, true)
            }}
          />
        ))
      )}
      <UpdateCareerPlanModal
        show={planToEdit && !milestoneToEdit}
        planToEdit={planToEdit}
        onClose={() => setPlanToEdit(null)}
        onSave={onSavePlan}
      />
      <UpdateMilestoneModal
        show={milestoneToEdit}
        milestoneToEdit={milestoneToEdit}
        onClose={() => {setPlanToEdit(null); setMilestoneToEdit(null);}}
        onSave={onSaveMilestone}
      />
    </section>
  )
}

export default CareerPlans
